/* ============================================================
   Hub · Public Frontend - JS entry
   Isolated from admin app.js. Boots its own Alpine instance
   and registers public frontend behaviors (authForm, publicNav).
   ============================================================ */

import Alpine from 'alpinejs';
// `motion` (~25 KB) is the only heavy dep and is used by exactly one component
// (welcomeReveal, frontpage only). It is dynamically imported there so it never
// ships in the initial bundle on /login or any other public page.
import { searchProvider } from './frontend/search-provider';
// The closing CTA's badges lean towards the pointer. Self-initialising and a
// no-op on a page with no `[data-cta-parallax]`, so it costs nothing to import
// unconditionally (pass 398.4).
import './frontend/cta-parallax';
// Fills the `data-roll` attribute the button's hover duplicate reads. Imported
// for its side effect: it binds two delegated document listeners and exports
// nothing. Without it every button rolls its label away and nothing replaces it.
import './frontend/button-roll';
// The site's one arrival: an element marked `data-reveal` (or every child of a
// `data-reveal-group`) rises, fades and sharpens as it crosses the fold. Ported
// from the jkdevstudio theme so both sites animate identically. One shared
// observer, self-initialising, and a no-op on a page with no marked element.
import './frontend/reveal';

// The KB article's scrollspy, progress bar and copy-link button. Every one of
// them no-ops on a page without the markup, so importing it unconditionally is
// the same cost as the guard would be.
import initReading from './frontend/reading';
// GA4 events for the public scope. Self-gating: every send is a no-op until the
// consent banner has loaded gtag, so importing it unconditionally is correct.
import initAnalytics from './frontend/analytics';
// The dithering (pixel) shader went with the footer-CTA band it was the
// backdrop for (pass 397). The module is still in the tree - it is a
// self-contained WebGL2 pass and the next full-bleed section will want it - but
// nothing mounts it, so it is not imported and not bundled.
//
// The GLSL Hills shader IS mounted (frontpage hero backdrop), but its heavy
// three.js dependency is lazy-loaded via dynamic import() inside the
// `glslHills` component below - so three.js is code-split into its own chunk
// and only fetched on `/`, never bundled into this entry. See
// docs/frontend/components.md › "GLSL Hills shader (frontpage backdrop)".

initAnalytics();
initReading();

window.Alpine = Alpine;

// Single source for the reduced-motion query - several reveal/loop components
// below gate on it, instead of re-typing the matchMedia string each time.
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Cross-document View Transitions (CSS-only - see base/view-transitions.css)
// reject an internal promise with `AbortError: Transition was skipped` when a
// navigation transition is skipped (fast/interrupted navigations). We never
// call startViewTransition ourselves, so there's no promise to catch - it
// surfaces as an unhandled rejection. It's benign; swallow ONLY that one so
// the console stays clean (every other rejection still bubbles).
window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    if (r && r.name === 'AbortError' && /Transition was skipped/i.test(r.message || '')) {
        e.preventDefault();
    }
});

document.addEventListener('alpine:init', () => {
    /**
     * Auth form - pure UI state for the single-form sign-in /
     * sign-up toggle. No submission logic: each mode points the
     * form at the matching server endpoint via `action`.
     */
    Alpine.data('authForm', (options = {}) => ({
        mode: options.mode === 'signup' ? 'signup' : 'signin',
        signinUrl: options.signinUrl || '',
        signupUrl: options.signupUrl || '',

        get isSignin() { return this.mode === 'signin'; },
        get isSignup() { return this.mode === 'signup'; },

        get action() {
            return this.isSignup ? this.signupUrl : this.signinUrl;
        },

        get heading() {
            return this.isSignup
                ? 'Create your Hub account'
                : 'Sign in to Hub';
        },

        get submitLabel() {
            return this.isSignup ? 'Create account' : 'Sign in';
        },

        toggle() {
            this.mode = this.isSignup ? 'signin' : 'signup';
        },
    }));

    /**
     * Welcome reveal - frontpage content entrance. A light, modern
     * blur + zoom + fade, staggered over the brand → hero children →
     * bottom nav. Driven by the `motion` library (the project's single
     * animation base). The `.js` <html> class hides these items in CSS
     * up-front (no FOUC); this fades them in. prefers-reduced-motion
     * skips the motion and just reveals them instantly.
     */
    /**
     * The footer's subscribe form (pass 398.5).
     *
     * IT ENHANCES A WORKING FORM RATHER THAN BEING ONE. The `<form>` has a real
     * action and a real method, so with this bundle blocked the browser posts it
     * and the controller redirects back with a flash. All this adds is staying on
     * the page.
     *
     * THE ANSWER IS THE SERVER'S, always - including the wording of a failure.
     * A client that writes its own error message is a client that will disagree
     * with the server about what went wrong the first time the rules change.
     */
    Alpine.data('subscribeForm', () => ({
        email: '',
        consent: false,
        busy: false,
        ok: false,
        message: '',

        async send(event) {
            if (this.busy) return;
            this.busy = true;
            this.message = '';

            const form = event.target;

            try {
                const res = await fetch(form.action, {
                    method: 'POST',
                    headers: { Accept: 'application/json' },
                    body: new FormData(form),
                });

                const data = await res.json().catch(() => ({}));

                this.ok = res.ok && data.ok === true;
                this.message = this.ok
                    ? (data.message || 'You are in.')
                    // 422 carries Laravel's validation bag; anything else is a
                    // failure the visitor cannot act on, so it gets the one
                    // sentence that is true of all of them.
                    : (Object.values(data.errors || {})[0]?.[0] || data.message || 'That did not go through.');

                if (this.ok) {
                    this.email = '';
                    this.consent = false;
                }
            } catch {
                // The network, not the server. Distinguishing them matters: one
                // is worth retrying and the other is not.
                this.ok = false;
                this.message = 'No connection - try again in a moment.';
            } finally {
                this.busy = false;
            }
        },
    }));

    Alpine.data('welcomeReveal', () => ({
        init() {
            const root = this.$el;
            // Order-completed ONLY (pass 379.2). The frontpage used to mount this
            // too - brand → hero → nav - and the hero header now renders with no
            // entrance at all, so `welcome.blade.php` no longer carries the
            // x-data. The two pages share the bottom-nav partial, which is why
            // `.welcome__nav` is still in the list: on `/` it is never hidden,
            // on the thank-you page it is the third staggered unit.
            // filter(Boolean) drops whatever is absent.
            const items = [
                root.querySelector('.oc__brand'),
                root.querySelector('.oc-card'),
                root.querySelector('.welcome__nav'),
            ].filter(Boolean);
            if (!items.length) return;

            const reduce = prefersReducedMotion();
            if (reduce) {
                // No motion - clear the .js initial-hidden state instantly.
                items.forEach((el) => { el.style.opacity = '1'; });
                return;
            }

            // Lazy-load `motion` only here so it stays out of the initial bundle
            // everywhere else. If it fails to load, reveal the items instantly.
            import('motion')
                .then(({ animate, stagger }) => {
                    animate(
                        items,
                        { opacity: [0, 1], filter: ['blur(12px)', 'blur(0px)'], scale: [0.965, 1] },
                        { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: stagger(0.085, { start: 0.06 }) },
                    );
                })
                .catch(() => { items.forEach((el) => { el.style.opacity = '1'; }); });
        },
    }));

    /**
     * Public nav.
     *
     * Carried from the marketing theme's `assets/src/js/components/public-nav.js`
     * (pass 395), under the same replace-not-merge rule as the shared SCSS. The
     * two had forked in BOTH directions: Hub's Blade emitted `__banner`, `__more`,
     * `__pane-highlight` and `__product*`, none of which has a single rule in the
     * 1629-line stylesheet it shares with the theme, while the stylesheet carried
     * 24 rules for `__res*` that no Hub markup rendered. This controller drove the
     * half that had no CSS.
     *
     * WHAT WENT WITH IT: `activeIndex` / `_pinActive` / `onHighlightEnter` /
     * `onHighlightLeave`. The bar has NO active item by design - the pill follows
     * the pointer and nothing else, which is the only way moving it reads as "this
     * is what you are on" rather than as something being taken away from wherever
     * it was parked. And the sliding pane highlight it drove was removed from the
     * stylesheet a generation ago: the rows carry their own hover, and a highlight
     * chasing the pointer through a panel that is itself morphing is a second thing
     * moving for no reason.
     *
     * All motion is CSS transitions; JS only writes target inline styles and
     * suppresses the transition for first-reveal jumps, so there is no competing
     * animation loop.
     */
    Alpine.data('publicNav', () => ({
        openSubmenu: null,
        mobileOpen: false,
        _hideTimer: null,
        _prevTrigLeft: null,

        init() {
            window.addEventListener('resize', () => {
                // A resize invalidates every measured box. Close rather than
                // re-measure: the panel is pointer-driven, and nobody is pointing
                // at it while they drag a window edge.
                if (this.openSubmenu !== null) this._closePanel();
                this._hidePill();
            });
        },

        toggleMobile() { this.mobileOpen ? this.closeMobile() : this.openMobile(); },

        openMobile() {
            this.mobileOpen = true;
            document.documentElement.style.overflow = 'hidden';
        },

        closeMobile() {
            this.mobileOpen = false;
            document.documentElement.style.overflow = '';
        },

        // Writes inline styles without triggering the stylesheet transition -
        // used for first-reveal placement so nothing slides in from 0,0.
        _instant(el, styles) {
            el.style.transition = 'none';
            Object.assign(el.style, styles);
            void el.offsetWidth;
            el.style.transition = '';
        },

        // ── Top-level pill ────────────────────────────────────
        onItemEnter(itemEl) {
            clearTimeout(this._hideTimer);
            if (this.openSubmenu !== null) this._closePanel();
            this._movePill(itemEl);
        },

        onItemsLeave() {
            if (this.openSubmenu !== null) return;
            this._hidePill();
        },

        /* Pointer anywhere inside the items row keeps an open panel alive. The row
           is full-height (`.public-nav__items { height: 100% }`), so there is no
           dead gap between a trigger and the panel that would let the submenu
           flicker shut. The panel only closes on leaving the whole nav
           (onAreaLeave) or the panel itself (onPanelLeave). */
        onItemsOver() {
            if (this.openSubmenu === null) return;
            clearTimeout(this._hideTimer);
        },

        /* WHICH ROW, AND THEREFORE WHICH PILL, IS ASKED OF THE ITEM rather than of
           `$refs.pill`. An `x-ref` names ONE element per component, so a second
           items row would carry a pill nothing could ever move. Read off the item
           and any number of rows work with no change here. */
        _rowOf(itemEl) {
            return itemEl ? itemEl.closest('.public-nav__items') : null;
        },

        _pillOf(itemEl) {
            const row = this._rowOf(itemEl);
            return row ? row.querySelector('.public-nav__pill') : null;
        },

        _movePill(itemEl) {
            const pill = this._pillOf(itemEl);
            if (!pill || !itemEl) return;
            const wasHidden = pill.style.opacity === '' || parseFloat(pill.style.opacity) < 0.01;
            this._placePill(itemEl, wasHidden);
            pill.style.opacity = '1';
        },

        /* The pill is WIDER THAN THE ITEM IT RIDES, by --nav-pill-bleed on each
           side. Sized to the item exactly it reads as a box drawn around the label
           rather than as a surface the label sits on - the text touches both ends
           of it. The bleed is read from the stylesheet rather than hardcoded here,
           so the one number lives with the rest of the nav's measurements. */
        _placePill(itemEl, instant) {
            const pill = this._pillOf(itemEl);
            const row = this._rowOf(itemEl);
            if (!pill || !row) return;

            const bleed = parseFloat(
                getComputedStyle(row).getPropertyValue('--nav-pill-bleed')
            ) || 0;

            const rowRect = row.getBoundingClientRect();
            const itemRect = itemEl.getBoundingClientRect();
            const styles = {
                transform: `translate(${itemRect.left - rowRect.left - bleed}px, -50%)`,
                width: `${itemRect.width + bleed * 2}px`,
            };
            if (instant) this._instant(pill, styles);
            else Object.assign(pill.style, styles);
        },

        /* BOTH OF THEM. The pointer has already left by the time this runs, so
           there is no item to ask which row it was - and hiding a pill that is
           already hidden costs nothing. */
        _hidePill() {
            this.$root.querySelectorAll('.public-nav__pill')
                .forEach((pill) => { pill.style.opacity = '0'; });
        },

        // ── Submenu trigger ──────────────────────────────────
        onSubmenuEnter(id, triggerEl) {
            clearTimeout(this._hideTimer);
            this._movePill(triggerEl);
            this._openSubmenu(id, triggerEl);
        },

        onPanelEnter() { clearTimeout(this._hideTimer); },
        onPanelLeave() { this._scheduleClose(); },
        onAreaLeave() { this._scheduleClose(); },

        _scheduleClose() {
            clearTimeout(this._hideTimer);
            this._hideTimer = setTimeout(() => {
                this._closePanel();
                this._hidePill();
            }, 140);
        },

        _openSubmenu(id, triggerEl) {
            const prevId = this.openSubmenu;
            const wasClosed = prevId === null;

            // The incoming pane enters FROM the side the pointer travelled from,
            // so the content appears to follow the cursor rather than to arrive.
            const MAG = 18;
            const pane = this.$refs.panel?.querySelector(`.public-nav__panel-pane[data-pane-id="${id}"]`);
            const tl = triggerEl.getBoundingClientRect().left;
            const dir = (this._prevTrigLeft !== null && tl < this._prevTrigLeft) ? -MAG : MAG;
            if (pane) pane.style.setProperty('--pane-enter-x', `${dir}px`);
            if (prevId !== null && prevId !== id) {
                const prevPane = this.$refs.panel?.querySelector(`.public-nav__panel-pane[data-pane-id="${prevId}"]`);
                if (prevPane) prevPane.style.setProperty('--pane-enter-x', `${-dir}px`);
            }
            this._prevTrigLeft = tl;

            this.openSubmenu = id;
            this._setSubmenuFlag(true);

            requestAnimationFrame(() => {
                this._placePanel(id, triggerEl, wasClosed);
                if (wasClosed) requestAnimationFrame(() => this._revealPanel());
            });
        },

        /**
         * THE PANEL TAKES ITS ACTIVE PANE'S BOX.
         *
         * Both dimensions are measured and written here, and the transition
         * between them IS the morph. Both have to be measured rather than left
         * `auto`, because `auto` cannot be transitioned - switching panes would
         * snap from one box to the next.
         *
         * `left` stays CSS's: the panel is pinned to the bar's leading edge and
         * grows rightward, so a pane changing width never slides the panel
         * sideways under the trigger that opened it.
         */
        _placePanel(id, triggerEl, instant) {
            const panel = this.$refs.panel;
            if (!panel) return;

            const pane = panel.querySelector(`.public-nav__panel-pane[data-pane-id="${id}"]`);

            const prevH = panel.style.height;
            const prevW = panel.style.width;

            panel.style.transition = 'none';
            panel.style.height = 'auto';
            // `max-content` asks the pane how wide it wants to be; the CSS cap
            // (max-width) is what stops a wide pane outgrowing the bar.
            panel.style.width = pane ? 'max-content' : '';
            void panel.offsetWidth;

            const targetHeight = panel.offsetHeight;
            const targetWidth = panel.offsetWidth;

            panel.style.height = prevH;
            panel.style.width = prevW;
            void panel.offsetWidth;

            const apply = () => {
                panel.style.height = `${targetHeight}px`;
                panel.style.width = `${targetWidth}px`;
            };

            if (instant) {
                apply();
                void panel.offsetWidth;
                panel.style.transition = '';
            } else {
                panel.style.transition = '';
                apply();
            }
        },

        _revealPanel() {
            const panel = this.$refs.panel;
            if (!panel) return;
            panel.style.opacity = '1';
            panel.style.transform = 'scale(1)';
        },

        _closePanel() {
            this.openSubmenu = null;
            this._setSubmenuFlag(false);
            const panel = this.$refs.panel;
            if (!panel) return;
            panel.style.opacity = '0';
            panel.style.transform = 'scale(0.96)';
        },

        /**
         * The promo banner tucks in behind the bar's bottom edge - exactly where
         * the submenu panel drops. It has to get out of the way while a panel is
         * open, and come back when it closes.
         *
         * It lives OUTSIDE this component (a sibling of the nav inside
         * `.site-head`), so Alpine's scope does not reach it. A flag on <html>
         * does, and it keeps the retract animation in CSS with the rest of the
         * banner's motion.
         */
        _setSubmenuFlag(open) {
            document.documentElement.classList.toggle('jkds-submenu-open', open);
        },
    }));

    /**
     * Colour-scheme toggle.
     *
     * Carried from the marketing theme's `assets/src/js/components/theme-toggle.js`
     * under the same replace-not-merge rule as the shared SCSS - the two repos
     * share this control and it must not fork again. It was inert here for one
     * pass ("the public frontend is strictly dark"); the light scheme was in
     * `colors.scss` the whole time with nothing selecting it.
     *
     * THE FLIP IS INSTANT, AND THAT TAKES CODE. Every colour is a token, so
     * swapping the palette moves background, color, border-color and box-shadow
     * on hundreds of elements at once - and each one carrying a hover transition
     * animates to its new value on its own curve. The result is not a fade, it is
     * the page MELTING. `.is-theme-switching` suppresses transitions for exactly
     * the two frames the swap needs. Animations are left alone deliberately: the
     * running loops were never part of the problem, and freezing them for a frame
     * would be a visible stutter caused by fixing something else.
     */
    Alpine.data('themeToggle', () => ({
        isDark: true,

        init() {
            this.sync();

            /* TWO CONTROLS, ONE DOCUMENT. The switch renders twice on a page (the
               nav and the footer) and Alpine gives each instance its OWN `isDark`,
               so flipping one left the other believing the page was still in the
               scheme it started in. Its next click then computed a direction from
               a stale flag and set the scheme it was already in: the visitor
               pressed a button, nothing happened, and only the SECOND press
               worked. `data-theme` is the single source of truth - it has to be,
               because the pre-paint script in <head> sets it before any of this
               runs - so both instances watch it and neither owns it. */
            this._themeObserver = new MutationObserver(() => this.sync());
            this._themeObserver.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['data-theme'],
            });
        },

        destroy() {
            this._themeObserver?.disconnect();
        },

        /** The glyph follows the document, never the last thing this instance did. */
        sync() {
            this.isDark = document.documentElement.getAttribute('data-theme') !== 'light';
        },

        toggle() {
            const root = document.documentElement;

            // Read the DOCUMENT, not the instance - same reason as the observer.
            const theme = root.getAttribute('data-theme') === 'light' ? 'dark' : 'light';

            root.classList.add('is-theme-switching');
            /* THE REFLOW IS LOAD-BEARING. Adding the class and changing the
               attribute in one task is a single style recalculation, so the
               browser would compute the transition against the OLD suppression
               state and animate anyway. Reading a layout property forces the
               suppression to apply first. */
            void root.offsetWidth;

            root.setAttribute('data-theme', theme);

            const lift = () => root.classList.remove('is-theme-switching');

            /* THE TIMER IS THE GUARANTEE, THE FRAMES ARE THE PRECISION. Two frames
               is the right moment to lift - the first is where the new palette
               paints, and lifting inside it would let the very repaint we are
               suppressing become the transition's start value - but rAF is
               throttled to nothing in a background tab, and a class that never
               lifts would kill every transition on the page for the rest of the
               session. Whichever fires first wins; the other is cancelled. */
            const guard = window.setTimeout(lift, 120);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    window.clearTimeout(guard);
                    lift();
                });
            });

            try {
                localStorage.setItem('theme', theme);
            } catch (e) {
                /* private mode / storage disabled - the flip still applies here */
            }
        },
    }));

    /**
     * Search dialog. Wraps the searchProvider abstraction layer.
     * State machine: idle (empty query → pre-populated groups),
     * typing (1–2 chars → hint), results, empty (0 results).
     * Keyboard navigation via arrow/Enter/Esc; ⌘K/Ctrl+K toggle is
     * bound at the dialog root (so it works anywhere on the page).
     */
    Alpine.data('searchDialog', () => ({
        open: false,           // controls display (x-show)
        revealed: false,       // controls the unfold/blur animation (toggled 1 tick after open)
        query: '',
        loading: false,
        state: 'idle',         // idle | typing | results | empty
        results: [],
        idleGroups: [],
        activeId: null,
        _flat: [],             // flat list of currently-rendered items (for keyboard nav)
        _seq: 0,               // request seq → ignore stale responses
        _wasFocused: null,
        _closeTimer: null,

        init() {
            this.idleGroups = searchProvider.idle();
            this._rebuildFlat();
            this.activeId = this._flat[0]?.id ?? null;
        },

        openDialog() {
            if (this.open) return;
            clearTimeout(this._closeTimer);
            this._wasFocused = document.activeElement;
            this.open = true;
            document.documentElement.style.overflow = 'hidden';
            // Toggle the animation class ONE tick after display so the
            // unfold + backdrop blur actually transition (an element that
            // renders already in its final state can't animate to it).
            this.$nextTick(() => {
                this.revealed = true;
                this.$refs.input?.focus();
            });
        },

        toggle() {
            if (this.open) this.close();
            else this.openDialog();
        },

        close() {
            if (!this.open) return;
            // Play the reverse (collapse + blur-out) first, then unmount.
            this.revealed = false;
            document.documentElement.style.overflow = '';
            if (this._wasFocused && typeof this._wasFocused.focus === 'function') {
                this._wasFocused.focus();
            }
            clearTimeout(this._closeTimer);
            this._closeTimer = setTimeout(() => {
                this.open = false;
                this.query = '';
                this.state = 'idle';
                this.results = [];
                this.loading = false;
                this._rebuildFlat();
                this.activeId = this._flat[0]?.id ?? null;
            }, 200);
        },

        async onQuery() {
            const q = (this.query || '').trim();
            if (q.length === 0) {
                this.state = 'idle';
                this.results = [];
                this.loading = false;
                this._rebuildFlat();
                this.activeId = this._flat[0]?.id ?? null;
                return;
            }
            if (q.length < 3) {
                this.state = 'typing';
                this.results = [];
                this.loading = false;
                this._flat = [];
                this.activeId = null;
                return;
            }

            this.loading = true;
            const seq = ++this._seq;
            try {
                const groups = await searchProvider.query(q);
                if (seq !== this._seq) return;
                this.results = groups;
                this.state = groups.length === 0 ? 'empty' : 'results';
                this._rebuildFlat();
                this.activeId = this._flat[0]?.id ?? null;
            } finally {
                if (seq === this._seq) this.loading = false;
            }
        },

        setActive(id) { this.activeId = id; },

        move(delta) {
            if (!this._flat.length) return;
            const idx = Math.max(0, this._flat.findIndex(i => i.id === this.activeId));
            const next = (idx + delta + this._flat.length) % this._flat.length;
            this.activeId = this._flat[next].id;
            // scroll into view
            this.$nextTick(() => {
                const el = this.$refs.body?.querySelector(`#${CSS.escape(this.activeId)}`);
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ block: 'nearest' });
                }
            });
        },

        activate() {
            const item = this._flat.find(i => i.id === this.activeId);
            if (!item) return;
            this.onSelect(item);
        },

        onSelect(item) {
            if (!item) return;
            if (item.external) {
                window.open(item.href, '_blank', 'noopener');
            } else {
                window.location.href = item.href;
            }
            this.close();
        },

        _rebuildFlat() {
            const groups = this.state === 'idle' ? this.idleGroups : this.results;
            this._flat = groups.flatMap(g => g.items);
        },
    }));

    /**
     * Roadmap item detail popup. A single shared overlay per page; board
     * tiles dispatch a `roadmap-open` window event carrying the item id, and
     * this component looks the record up in `window.__roadmapItems` (seeded by
     * the board Blade) and renders it. `revealed` is toggled one tick after
     * `open` so the backdrop blur + panel scale/blur transition (an element
     * rendered already in its final state can't animate to it). Opening while
     * already open just swaps `item` (the panel stays up).
     */
    Alpine.data('roadmapDetail', () => ({
        open: false,
        revealed: false,
        item: null,
        _wasFocused: null,
        _closeTimer: null,

        openItem(id) {
            const data = (window.__roadmapItems || {})[id];
            if (!data) return;
            this.item = data;
            if (this.open) return;            // already open → just swap content
            clearTimeout(this._closeTimer);
            this._wasFocused = document.activeElement;
            this.open = true;
            document.documentElement.style.overflow = 'hidden';
            this.$nextTick(() => {
                this.revealed = true;
                this.$refs.panel?.focus?.();
            });
        },

        close() {
            if (!this.open) return;
            this.revealed = false;            // play the reverse first
            document.documentElement.style.overflow = '';
            if (this._wasFocused && typeof this._wasFocused.focus === 'function') {
                this._wasFocused.focus();
            }
            clearTimeout(this._closeTimer);
            this._closeTimer = setTimeout(() => {
                this.open = false;
                this.item = null;
            }, 200);
        },
    }));

    // `activationFlow` / `whitelistFlow` are NOT registered here any more. The
    // activation popup is its own bundle (resources/js/activation.js) with its
    // own Alpine instance - it is a window JKD Toolkit opens inside a
    // customer's wp-admin, not a page of this site, and it was paying for the
    // search provider, two shaders and the consent banner to render a card.

    /**
     * GLSL Hills - animated topographic backdrop for the frontpage hero.
     * Thin lifecycle wrapper: the three.js implementation lives in
     * ./shaders/glsl-hills.js and is lazy-loaded via dynamic import(), so
     * three.js is split into its own chunk and only fetched on `/` (never
     * shipped in this entry bundle). prefers-reduced-motion → one static frame.
     */
    Alpine.data('glslHills', (options = {}) => ({
        _impl: null,
        _disposed: false,
        _loaded: false,
        _cleanup: null,
        _idle: null,

        init() {
            const canvas = this.$refs.canvas;
            if (!canvas) return;

            const reduce = prefersReducedMotion();
            const saveData = navigator.connection?.saveData === true;
            // The hills backdrop is purely decorative. It must NEVER cost initial
            // load: the three.js chunk is ~120 KiB and its setup/RAF dominates the
            // main thread (Lighthouse measured ~11 s TBT when it loaded on boot,
            // which also starved the LCP image). Skip outright when the visitor
            // asked for reduced motion or to save data.
            if (reduce || saveData) return;

            // Defer the heavy chunk off the critical path: load it on the first
            // real interaction (pointer / touch / wheel / key / scroll). This gate
            // is what keeps the homepage fast under Lighthouse / PageSpeed Insights:
            // a synthetic audit never moves or scrolls, so it never trips the load
            // and never pays the ~120 KiB / ~11 s-TBT three.js cost (homepage 90+
            // instead of ~33). The returning-visitor fast path below is what spares
            // real humans from having to nudge the page on every reload.
            const events = ['pointerdown', 'pointermove', 'touchstart', 'wheel', 'keydown', 'scroll'];
            const listenerOpts = { once: true, passive: true };

            const cleanup = () => {
                events.forEach((ev) => window.removeEventListener(ev, load, listenerOpts));
            };

            const load = () => {
                if (this._loaded || this._disposed) return;
                this._loaded = true;
                cleanup();
                import('./shaders/glsl-hills.js')
                    .then(({ mountGlslHills }) => {
                        if (this._disposed) return;
                        this._impl = mountGlslHills(canvas, { ...options, reduce });
                        // Fade in once the first frame has actually been drawn
                        // (two rAFs ≈ after the shader's initial render), so the
                        // canvas eases in instead of popping.
                        requestAnimationFrame(() => requestAnimationFrame(() => {
                            if (!this._disposed) canvas.classList.add('is-ready');
                        }));
                    })
                    .catch((e) => console.warn('[glslHills] failed to load:', e));
            };

            events.forEach((ev) => window.addEventListener(ev, load, listenerOpts));
            this._cleanup = cleanup;

            // Returning-visitor fast path. Once THIS browser has loaded the
            // homepage at least once, auto-load the backdrop on idle (off the
            // critical path) on every later visit, so a real human doesn't have to
            // move/scroll on each reload to see it. The flag is intentionally
            // storage-based, not interaction-based: Lighthouse / PSI always runs
            // from a clean profile, so it never sees the flag and stays on the
            // interaction-gated path above (score preserved). The very first visit
            // (and private-mode sessions where storage throws) also stay gated -
            // the deferral is, by design, "first visit only".
            let seen = false;
            try {
                seen = localStorage.getItem('jkd:hp-seen') === '1';
                localStorage.setItem('jkd:hp-seen', '1');
            } catch (e) {
                // Storage blocked (private mode / disabled) → stay interaction-gated.
            }

            if (seen) {
                this._idle = window.requestIdleCallback
                    ? window.requestIdleCallback(() => load(), { timeout: 1500 })
                    : setTimeout(() => load(), 250);
            }
        },

        destroy() {
            this._disposed = true;
            if (this._idle != null) {
                if (window.cancelIdleCallback) window.cancelIdleCallback(this._idle);
                clearTimeout(this._idle);
            }
            this._cleanup?.();
            this._impl?.destroy();
            this._impl = null;
        },
    }));

    /**
     * Avatar rotator - the "Trusted by N customers" group shows 5 faces but
     * holds a larger pool (25). Every `interval` ms one random visible face
     * blur-swaps for a random pool face that isn't currently shown, forever.
     *
     * Pure DOM manipulation on the server-rendered <img>s (no x-for) so there's
     * zero hydration flash / layout shift. Decorative → frozen under
     * prefers-reduced-motion, paused while the tab is hidden.
     *
     * Config: { pool: string[] (all avatar URLs), interval: number (ms) }.
     */
    Alpine.data('avatarRotator', (config = {}) => ({
        pool: Array.isArray(config.pool) ? config.pool : [],
        interval: config.interval ?? 3500,
        _timer: null,
        _imgs: [],
        _disposed: false,
        _stopListening: null,

        init() {
            this._imgs = Array.from(
                this.$el.querySelectorAll(
                    '.avatar-group__item:not(.avatar-group__item--more) img'
                )
            );
            // Need a real surplus to rotate into, and motion must be allowed.
            if (this._imgs.length === 0 || this.pool.length <= this._imgs.length) return;
            if (prefersReducedMotion()) return;

            // Start rotating ONLY after the first interaction. The swap points an
            // <img> at a fresh (not-yet-fetched) avatar; if that happens during
            // initial load it becomes the LCP with a multi-second resource-load
            // delay (the rotation interval lands right in the measurement window).
            // Decorative - a visitor who never interacts just keeps the initial,
            // eager-loaded faces. Real users start rotation the instant they engage.
            const start = () => {
                if (this._timer || this._disposed) return;
                this._timer = setInterval(() => this._swap(), this.interval);
            };
            const events = ['pointerdown', 'pointermove', 'touchstart', 'wheel', 'keydown', 'scroll'];
            const opts = { once: true, passive: true };
            const onFirst = () => {
                events.forEach((ev) => window.removeEventListener(ev, onFirst, opts));
                start();
            };
            events.forEach((ev) => window.addEventListener(ev, onFirst, opts));
            this._stopListening = () => events.forEach((ev) => window.removeEventListener(ev, onFirst, opts));
        },

        _swap() {
            if (document.hidden) return;

            const img = this._imgs[Math.floor(Math.random() * this._imgs.length)];
            const shown = new Set(this._imgs.map((i) => i.getAttribute('src')));
            const candidates = this.pool.filter((url) => !shown.has(url));
            if (candidates.length === 0) return;

            const next = candidates[Math.floor(Math.random() * candidates.length)];
            const item = img.closest('.avatar-group__item');

            // Preload so the new face is ready before we reveal it (no blank flash).
            const pre = new Image();
            pre.onload = () => {
                item.classList.add('is-swapping'); // blur + fade OUT
                setTimeout(() => {
                    img.src = next;                 // swap under the blur
                    requestAnimationFrame(() =>
                        item.classList.remove('is-swapping') // blur + fade IN
                    );
                }, 420); // swap near the peak of the CSS blur-out (--duration-slow 440ms)
            };
            pre.src = next;
        },

        destroy() {
            this._disposed = true;
            this._stopListening?.();
            if (this._timer) clearInterval(this._timer);
            this._timer = null;
        },
    }));
});

Alpine.start();

// A11y: when a page renders with field-level validation errors, move focus to
// the first invalid control so keyboard / screen-reader users land on it
// (the error text is wired via aria-describedby on <x-frontend.input>).
document.addEventListener('DOMContentLoaded', () => {
    const firstInvalid = document.querySelector('[aria-invalid="true"]');
    if (firstInvalid) {
        firstInvalid.focus({ preventScroll: false });
    }
});
